import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { AnimatePresence, motion } from "framer-motion";
import { apiGet, apiPost, apiDelete, type AuthUser } from "../api.js";
import { AuthedImage } from "../components/AuthedImage.js";
import { MarkerEditor, type DeltaPoint, type Point } from "../components/MarkerEditor.js";
import { UploadFingerCard } from "../components/UploadFingerCard.js";
import { Badge, Button, Card } from "../components/ui/index.js";
import { statusLabel, t } from "../i18n.js";
import { EXPECTED_DELTAS, FINGERS, FINGER_LABEL_ID, type FingerKey, type KlienAnalysisResponse, PATTERNS, type Pattern } from "@dfa/types";
import { Loader2 } from "lucide-react";

interface Props {
  user: AuthUser;
}

const PROCESSING_STATUSES = ["pending", "stitched"];
const FAILED_STATUSES = ["stitch_failed", "vision_failed"];
const SIDE_LABEL: Record<string, string> = { F: "Depan", L: "Kiri", R: "Kanan" };

/** Card for a finger marked cacat (defect/absent) — excluded from "jari diproses",
 *  no upload slots; operator can undo a mis-press. */
function DefectiveFingerCard({ label, canEdit, onUnmark, busy }: { label: string; canEdit: boolean; onUnmark: () => void; busy: boolean }) {
  return (
    <div className="card flex items-center justify-between gap-3 border-slate-300 bg-slate-50">
      <div className="flex items-center gap-3">
        <span className="font-medium text-slate-700 w-28 shrink-0">{label}</span>
        <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
          <span className="px-2 py-0.5 rounded-md bg-slate-200 text-slate-600 text-xs font-semibold">Cacat / tidak ada</span>
          tidak dihitung dalam jari wajib
        </span>
      </div>
      {canEdit && (
        <button type="button" onClick={onUnmark} disabled={busy} className="text-xs text-slate-500 hover:text-brand-600 underline disabled:opacity-50">
          {busy ? "…" : "Batalkan tanda cacat"}
        </button>
      )}
    </div>
  );
}

/** Staged "sedang diproses" card shown while a finger is stitching/analyzing.
 *  HONEST staged labels (no fake %): backend exposes status transitions, not
 *  granular job progress. */
function ProcessingFingerCard({ label, status }: { label: string; status: string }) {
  const failed = FAILED_STATUSES.includes(status);
  const stage = status === "pending" ? "Menggabung foto…" : status === "stitched" ? "Menganalisa pola…" : "";
  const errMsg = status === "stitch_failed" ? "Gagal menggabung foto" : "Gagal menganalisa pola";
  return (
    <div className={`card flex items-center gap-3 ${failed ? "border-red-200 bg-red-50" : ""}`}>
      <span className="font-medium text-brand-900 w-28 shrink-0">{label}</span>
      {failed ? (
        <span className="text-sm text-tabrakan">⚠ {errMsg} — coba upload ulang sisi jari ini.</span>
      ) : (
        <span className="inline-flex items-center gap-2 text-sm text-slate-600">
          <Loader2 size={15} className="animate-spin text-brand-500" />
          {stage}
        </span>
      )}
    </div>
  );
}

export function Layer4Page({ user: _user }: Props) {
  const [, params] = useRoute<{ id: string }>("/klien/:id");
  const [, setLocation] = useLocation();
  const id = params?.id;
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery<KlienAnalysisResponse>({
    queryKey: ["klien-analysis", id],
    queryFn: () => apiGet(`api/klien/${id}/analysis`),
    enabled: !!id,
    // Poll faster (2.5s) while anything is still processing so the staged
    // indicator feels live; fall back to 8s once everything is analyzed.
    refetchInterval: (query) => {
      const d = query.state.data;
      const anyProcessing =
        d?.fingers?.some((f) => f.finger_status === "pending" || f.finger_status === "stitched") ||
        ["uploaded", "stitching", "stitched", "analyzing"].includes(d?.klien.status ?? "");
      return anyProcessing ? 2_500 : 8_000;
    },
  });

  // Un-mark a cacat finger → returns it to the upload phase.
  const unmarkCacat = useMutation({
    mutationFn: (finger: string) => apiDelete(`api/upload/${id}/finger/${finger}/cacat`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["klien-analysis", id] }),
  });

  const [showConfirmApprove, setShowConfirmApprove] = useState(false);
  const [initials, setInitials] = useState("");
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const [autoFocusedFinger, setAutoFocusedFinger] = useState<FingerKey | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Sorted fingers: lowest confidence first.
  const orderedFingers = useMemo(() => {
    if (!data) return [];
    return [...data.fingers].sort((a, b) => {
      const ca = a.confidence ?? 0;
      const cb = b.confidence ?? 0;
      return ca - cb;
    });
  }, [data]);

  const needsReview = useMemo(() => {
    return orderedFingers.filter((f) => (f.confidence ?? 0) < 0.92);
  }, [orderedFingers]);

  useEffect(() => {
    // Auto-scroll to lowest-confidence finger on first load.
    if (data && needsReview.length > 0 && !autoFocusedFinger) {
      const first = needsReview[0]!.finger;
      const el = cardRefs.current[first];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setAutoFocusedFinger(first);
      }
    }
  }, [data, needsReview, autoFocusedFinger]);

  const approveKlienMutation = useMutation({
    mutationFn: async (args: { initials: string; rating?: number }) => {
      return apiPost(`api/layer4/${id}/approve`, {
        klien_id: id,
        expected_version: data!.klien.version,
        klien_initials_confirmation: args.initials,
        reviewer_confidence_rating: args.rating,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["klien-analysis", id] });
      setShowConfirmApprove(false);
      setLocation("/");
    },
  });

  if (isLoading) return <div className="card text-center text-slate-500">{t.loading}</div>;
  if (error) return <div className="card text-tabrakan">Error: {String(error)}</div>;
  if (!data) return null;

  const expectedInitials = data.klien.full_name
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <div className="space-y-4 pb-32">
      {/* Klien identity header — persistent */}
      <KlienHeader klien={data.klien} />

      {/* Framing disclaimer banner */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.08 }}
        className="card bg-amber-50/70 border-amber-200 border-l-4 border-l-amber-400"
      >
        <div className="text-[10px] font-bold text-amber-900 uppercase tracking-wider mb-1">
          Catatan Framing
        </div>
        <p className="text-sm text-amber-900 leading-relaxed">{t.framingDisclaimer}</p>
      </motion.div>

      {/* Jump-to-problems banner */}
      <AnimatePresence>
        {needsReview.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="card bg-gradient-to-r from-amber-50 to-amber-50/50 border-amber-300 border-l-4 border-l-amber-500 sticky top-16 z-20 backdrop-blur-sm"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-amber-900">
                <strong className="inline-flex items-center gap-1.5">
                  <span aria-hidden>⚠</span> {needsReview.length} {t.jumpToProblems}:
                </strong>{" "}
                {needsReview.slice(0, 5).map((f, i) => (
                  <span key={f.finger}>
                    {i > 0 && ", "}
                    <button
                      type="button"
                      className="font-medium underline underline-offset-2 hover:text-brand-700 transition-colors"
                      onClick={() => cardRefs.current[f.finger]?.scrollIntoView({ behavior: "smooth", block: "center" })}
                    >
                      {FINGER_LABEL_ID[f.finger as FingerKey]} ({((f.confidence ?? 0) * 100).toFixed(0)}%)
                    </button>
                  </span>
                ))}
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => cardRefs.current[needsReview[0]!.finger]?.scrollIntoView({ behavior: "smooth", block: "center" })}
              >
                {t.startFromHere}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status + (optional) upload progress + draft saved */}
      <div className="flex items-center justify-between text-sm gap-3 flex-wrap">
        <span className="text-slate-600">Status: <strong>{statusLabel(data.klien.status)}</strong></span>
        {(() => {
          // Opsi X: jari boleh 1-3 sisi, jadi total bukan lagi /30. Hitung foto
          // ter-upload (apa adanya) + jari yang sudah DI-TRIGGER (punya row analisa).
          let totalSides = 0;
          for (const fk of FINGERS) {
            totalSides += data.upload_progress?.[fk]?.length ?? 0;
          }
          // Cacat dikecualikan dari hitung jari wajib: penyebut = 10 − cacat.
          const cacatCount = data.fingers.filter((x) => x.finger_status === "cacat").length;
          const triggered = data.fingers.length - cacatCount; // jari non-cacat dalam pipeline
          const denom = Math.max(10 - cacatCount, 1);
          // Only show upload progress while not yet "ready_for_review".
          const showProgress = !["ready_for_review", "approving", "approved", "reporting", "reported"].includes(data.klien.status);
          if (!showProgress) return null;
          // HONEST progress: jari selesai dianalisa / jari non-cacat yang sudah di-trigger.
          const analyzedDone = data.fingers.filter((x) => x.finger_status !== "cacat" && !PROCESSING_STATUSES.includes(x.finger_status) && !FAILED_STATUSES.includes(x.finger_status)).length;
          const processingNow = data.fingers.filter((x) => PROCESSING_STATUSES.includes(x.finger_status)).length;
          const expected = Math.max(triggered, 1);
          const pct = Math.round((analyzedDone / expected) * 100);
          return (
            <span className="text-xs text-slate-600 inline-flex items-center gap-2 flex-wrap">
              <span><strong className="text-brand-700">{totalSides}</strong> foto</span>
              <span className="text-slate-300">·</span>
              <span><strong className="text-brand-700">{triggered}</strong>/{denom} jari diproses</span>
              {cacatCount > 0 && <span className="text-slate-400">({cacatCount} cacat)</span>}
              {processingNow > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin text-brand-500" />
                    <span>{analyzedDone}/{expected} jari dianalisa ({pct}%)</span>
                    <span className="inline-block w-16 h-1.5 rounded-full bg-slate-200 overflow-hidden align-middle">
                      <span className="block h-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
                    </span>
                  </span>
                </>
              )}
            </span>
          );
        })()}
        {draftSavedAt && (
          <span className="text-xs text-slate-500">
            {t.draftSaved} {Math.round((Date.now() - draftSavedAt.getTime()) / 1000)} {t.secondsAgo}
          </span>
        )}
      </div>

      <div className="grid gap-4">
        {FINGERS.map((fingerKey) => {
          const f = data.fingers.find((x) => x.finger === fingerKey);
          if (!f) {
            // No finger_analyses row yet → upload phase.
            const sidesUploaded = (data.upload_progress?.[fingerKey] ?? []) as ("F" | "L" | "R")[];
            return (
              <div key={fingerKey} ref={(el) => { cardRefs.current[fingerKey] = el; }}>
                <UploadFingerCard
                  klienId={id!}
                  finger={fingerKey}
                  fingerLabel={FINGER_LABEL_ID[fingerKey]}
                  sidesUploaded={sidesUploaded}
                  disabled={!data.permissions.can_edit}
                  onUploaded={() => qc.invalidateQueries({ queryKey: ["klien-analysis", id] })}
                />
              </div>
            );
          }
          // Marked cacat → dedicated card (no analysis data), with un-mark.
          if (f.finger_status === "cacat") {
            return (
              <div key={fingerKey} ref={(el) => { cardRefs.current[fingerKey] = el; }}>
                <DefectiveFingerCard
                  label={FINGER_LABEL_ID[fingerKey]}
                  canEdit={data.permissions.can_edit}
                  busy={unmarkCacat.isPending}
                  onUnmark={() => unmarkCacat.mutate(fingerKey)}
                />
              </div>
            );
          }
          // Still stitching/analyzing (or failed) → show a staged indicator
          // instead of an empty FingerCard (which expects pattern/markers).
          if (PROCESSING_STATUSES.includes(f.finger_status) || FAILED_STATUSES.includes(f.finger_status)) {
            return (
              <div key={fingerKey} ref={(el) => { cardRefs.current[fingerKey] = el; }}>
                <ProcessingFingerCard label={FINGER_LABEL_ID[fingerKey]} status={f.finger_status} />
              </div>
            );
          }
          return (
            <div key={fingerKey} ref={(el) => { cardRefs.current[fingerKey] = el; }}>
              <FingerCard
                klienId={id!}
                finger={f}
                canEdit={data.permissions.can_edit}
                onSaved={() => setDraftSavedAt(new Date())}
              />
            </div>
          );
        })}
      </div>

      {/* Sticky bottom action bar */}
      {data.permissions.can_approve && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-surface-3 px-4 py-3 z-30 shadow-depth-3"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm flex items-center gap-3">
              <div className="font-semibold text-brand-900">
                <span className="text-2xl text-brand-500">{data.fingers.filter((f) => f.finger_status === "approved").length}</span>
                <span className="text-slate-400">/10</span>
                <span className="text-xs ml-1.5 text-slate-600 font-normal">jari approved</span>
              </div>
              {needsReview.length > 0 && (
                <Badge variant="warning">{needsReview.length} low confidence</Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setLocation("/")}>
                {t.back}
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowConfirmApprove(true)}
                disabled={data.klien.status === "approved" || data.klien.status === "reported"}
                iconRight={<span aria-hidden>→</span>}
              >
                {t.submitFinal}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Approve confirmation modal */}
      <AnimatePresence>
        {showConfirmApprove && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-brand-950/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowConfirmApprove(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="bg-white border border-surface-3 rounded-2xl shadow-depth-3 p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-semibold text-brand-900 mb-1.5">{t.finalSubmitTitle}</h2>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">{t.finalSubmitBody}</p>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                {t.typeInitials}
              </label>
              <input
                type="text"
                value={initials}
                onChange={(e) => setInitials(e.target.value.toUpperCase())}
                className="input mb-4 tracking-widest font-semibold text-center"
                placeholder={expectedInitials}
                autoFocus
              />
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Seberapa yakin Anda dengan review ini? <span className="text-slate-400">(opsional)</span>
              </label>
              <select
                value={rating ?? ""}
                onChange={(e) => setRating(e.target.value ? Number(e.target.value) : undefined)}
                className="input mb-4"
              >
                <option value="">— tidak diisi —</option>
                <option value="5">5 — sangat yakin</option>
                <option value="4">4 — yakin</option>
                <option value="3">3 — cukup yakin</option>
                <option value="2">2 — ragu</option>
                <option value="1">1 — sangat ragu</option>
              </select>
              {approveKlienMutation.isError && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5 mb-3"
                >
                  {String(approveKlienMutation.error)}
                </motion.div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setShowConfirmApprove(false)}>
                  {t.back}
                </Button>
                <Button
                  variant="primary"
                  disabled={initials.length === 0}
                  loading={approveKlienMutation.isPending}
                  onClick={() => approveKlienMutation.mutate({ initials, rating })}
                >
                  {t.submitFinal}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function KlienHeader({ klien }: { klien: KlienAnalysisResponse["klien"] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="card bg-gradient-to-r from-white via-brand-50/40 to-brand-50/20 border-brand-200/60"
    >
      <div className="flex gap-4 items-start">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center text-2xl font-semibold flex-shrink-0 shadow-depth-2">
          {klien.full_name[0]?.toUpperCase() ?? "?"}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-brand-900 truncate" title={klien.full_name}>
            {klien.full_name.length > 28 ? `${klien.full_name.slice(0, 28)}…` : klien.full_name}
          </h1>
          <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>DOB <strong className="text-slate-700">{String(klien.dob).slice(0, 10)}</strong></span>
            <span className="text-slate-300">·</span>
            <span>Batch <strong className="text-slate-700">{klien.batch_id}</strong></span>
            <span className="text-slate-300">·</span>
            <span>Scan {new Date(klien.scan_at).toLocaleString("id-ID")}</span>
          </div>
          {(klien.school_name || klien.parent_name) && (
            <div className="text-[11px] text-slate-500 mt-1">
              {klien.school_name ? `Sekolah: ${klien.school_name}` : ""}
              {klien.parent_name ? ` · Orang tua: ${klien.parent_name}` : ""}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 text-xs text-slate-500 shrink-0">
          <Badge variant="info">{klien.package_tier.toUpperCase()}</Badge>
          <div className="text-[10px] tracking-wide text-slate-400">{klien.algorithm_version}</div>
          {klien.is_left_handed && <Badge variant="warning">⟲ Kidal</Badge>}
        </div>
      </div>
    </motion.div>
  );
}

interface FingerCardProps {
  klienId: string;
  finger: KlienAnalysisResponse["fingers"][number];
  canEdit: boolean;
  onSaved: () => void;
}

function FingerCard({ klienId, finger, canEdit, onSaved }: FingerCardProps) {
  const qc = useQueryClient();
  const conf = finger.confidence ?? 0;
  const isHighConf = conf >= 0.92;
  const isLowConf = conf < 0.75;
  const isApproved = finger.finger_status === "approved";
  // Reviewer must explicitly tick "verified" before per-finger approve unlocks.
  const [verified, setVerified] = useState(false);
  // Classifier provenance for the explicit "DRAFT AI" signal.
  const sourceLabel = (finger.algorithm_version ?? "").startsWith("cnn") ? "CNN" : "Claude";

  const [expanded, setExpanded] = useState(!isHighConf || isLowConf);
  const [pattern, setPattern] = useState<Pattern>(finger.pattern);
  const [ridgeL, setRidgeL] = useState(finger.ridge_count_left);
  const [ridgeR, setRidgeR] = useState(finger.ridge_count_right);
  const subVersionRef = useRef(finger.sub_version);

  // Debounced autosave.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editMutation = useMutation({
    mutationFn: async (changes: {
      pattern?: Pattern;
      ridge_count_left?: number;
      ridge_count_right?: number;
      core?: Point | null;
      deltas?: DeltaPoint[];
    }) => {
      return apiPost<{ sub_version: number; applied: boolean }>(`api/layer4/finger/edit`, {
        klien_id: klienId,
        finger: finger.finger,
        edit_id: crypto.randomUUID(),
        expected_sub_version: subVersionRef.current,
        changes,
      });
    },
    onSuccess: (data: { sub_version: number; applied: boolean }) => {
      subVersionRef.current = data.sub_version;
      onSaved();
      qc.invalidateQueries({ queryKey: ["klien-analysis", klienId] });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      return apiPost(`api/layer4/finger/approve`, { klien_id: klienId, finger: finger.finger });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["klien-analysis", klienId] }),
  });

  function scheduleSave(changes: Parameters<typeof editMutation.mutate>[0]) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => editMutation.mutate(changes), 800);
  }

  // After marker drag (+ snap) — call backend recount that uses the same
  // Gabor skeleton as analyze.ts → ridge counts stay consistent (no jumps).
  const [isRecounting, setIsRecounting] = useState(false);
  async function handleMarkerChange(changes: { core?: Point | null; deltas?: DeltaPoint[] }) {
    const nextCore = changes.core ?? (finger.core as Point | null);
    const nextDeltas = changes.deltas ?? ((finger.deltas ?? []) as DeltaPoint[]);
    setIsRecounting(true);
    try {
      const recount = await apiPost<{ ridge_count_left: number; ridge_count_right: number }>(
        "api/layer4/finger/recount",
        {
          klien_id: klienId,
          finger: finger.finger,
          core: nextCore,
          deltas: nextDeltas,
        }
      );
      setRidgeL(recount.ridge_count_left);
      setRidgeR(recount.ridge_count_right);
      scheduleSave({
        ...changes,
        ridge_count_left: recount.ridge_count_left,
        ridge_count_right: recount.ridge_count_right,
      });
    } catch (err) {
      // If recount fails, still persist the geometry change so position isn't lost.
      console.warn("recount failed, saving position only", err);
      scheduleSave(changes);
    } finally {
      setIsRecounting(false);
    }
  }

  const cardAccent: "success" | "danger" | "warning" | "none" = isApproved
    ? "success"
    : isLowConf
      ? "danger"
      : !isHighConf
        ? "warning"
        : "none";
  const confVariant: "success" | "warning" | "danger" | "neutral" = isApproved
    ? "success"
    : isLowConf
      ? "danger"
      : !isHighConf
        ? "warning"
        : "neutral";

  return (
    <Card accent={cardAccent} animate className="hover:shadow-depth-2 transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-semibold text-sm text-brand-900">{FINGER_LABEL_ID[finger.finger as FingerKey]}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{statusLabel(finger.finger_status)}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-1.5">
            <Badge variant={confVariant}>{(conf * 100).toFixed(0)}%</Badge>
            {finger.validation_warnings?.some((w) => w.toLowerCase().includes("stitch")) && <Badge variant="warning">⚠ stitch</Badge>}
          </div>
          {!isApproved && (
            <Badge variant="warning" className="font-semibold">DRAFT AI — REVIEW DULU</Badge>
          )}
        </div>
      </div>

      {/* Explicit pattern provenance so a high CNN confidence never reads as "verified". */}
      {!isApproved && (
        <div className="mb-3 text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          Usulan AI: <strong>{finger.pattern}</strong> · {sourceLabel} {conf.toFixed(2)} — <span className="font-medium">belum diverifikasi</span>
        </div>
      )}

      {(expanded || !isHighConf) && (
        <>
          {/* #2 layout (tanpa-merge): ATAS = 2 kolom — KIRI jari-utuh (composite
              F|L|R) + marker, KANAN gabor besar. BAWAH = 3 sisi mentah. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-2 items-start">
          {/* KIRI — HASIL + MARKER (composite 1-3 panel + marker overlay di panel geometry). */}
          {(() => {
            const panelSides = (finger.sides_present && finger.sides_present.length > 0
              ? finger.sides_present
              : ["F", "L", "R"]) as ("F" | "L" | "R")[];
            const n = Math.max(panelSides.length, 1);
            const w = 100 / n;
            const geomIdx = Math.min(Math.max(finger.geometry_side_index ?? 0, 0), n - 1);
            const geomLabel = SIDE_LABEL[panelSides[geomIdx] ?? "F"];
            return (
              <figure className="space-y-1.5">
                <div className="flex justify-between items-baseline">
                  <figcaption className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Hasil + Marker</figcaption>
                  <span className="text-[10px] text-slate-500 italic">
                    {n === 1 ? "1 sisi" : `${n} sisi`} — marker dianalisis dari sisi {geomLabel}.
                  </span>
                </div>
                <div
                  className="bg-white border border-surface-3 rounded-xl overflow-hidden shadow-depth-1 relative mx-auto"
                  style={{ aspectRatio: (400 * n) / 500, maxWidth: "100%" }}
                >
                  {finger.stitched_image_url && (
                    <AuthedImage
                      url={finger.stitched_image_url}
                      alt="composite"
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  )}
                  {/* Dynamic dividers between the n panels. */}
                  {panelSides.slice(1).map((_, i) => (
                    <div key={`div-${i}`} aria-hidden className="absolute inset-y-0 pointer-events-none" style={{ left: `${(i + 1) * w}%`, width: 1, background: "rgba(15,42,8,0.18)" }} />
                  ))}
                  {/* Dynamic per-panel labels. */}
                  {panelSides.map((side, i) => (
                    <span key={`lbl-${side}`} className="absolute bottom-1 text-[10px] font-semibold tracking-wider uppercase text-slate-600 bg-white/80 px-1.5 py-0.5 rounded-md backdrop-blur-sm pointer-events-none" style={{ left: `calc(${i * w}% + 4px)` }}>
                      {SIDE_LABEL[side]}
                    </span>
                  ))}
                  {/* Marker overlay on the geometry panel. */}
                  {finger.regenerate_url && (
                    <div className="absolute inset-y-0" style={{ left: `${geomIdx * w}%`, width: `${w}%` }}>
                      <MarkerEditor
                        skeletonUrl={finger.regenerate_url}
                        core={finger.core as Point | null}
                        deltas={(finger.deltas ?? []) as DeltaPoint[]}
                        canEdit={canEdit && !isApproved}
                        onChange={(changes) => handleMarkerChange(changes)}
                        hideSkeleton
                      />
                    </div>
                  )}
                </div>
              </figure>
            );
          })()}

          {/* KANAN — Gabor (Ridge Halus) diperbesar. */}
          <figure className="space-y-1.5">
            <figcaption className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Gabor (Ridge Halus)</figcaption>
            <div className="aspect-[4/5] bg-white border border-surface-3 rounded-xl overflow-hidden flex items-center justify-center shadow-depth-1 mx-auto" style={{ maxWidth: "100%" }}>
              {finger.gabor_smooth_url ? (
                <AuthedImage
                  url={finger.gabor_smooth_url}
                  alt="gabor ridge halus"
                  className="max-w-full max-h-full object-contain"
                  fallback={<span className="text-[10px] text-slate-400 text-center p-2">belum tersedia<br />(klien baru atau re-process)</span>}
                />
              ) : (
                <span className="text-[10px] text-slate-400 text-center p-2">belum tersedia</span>
              )}
            </div>
          </figure>
          </div>

          {/* BOTTOM — 3 foto sisi MENTAH yang di-upload (hanya yang ada; dilewati → kosong). */}
          <div className="grid grid-cols-3 gap-3 mt-3 max-w-xl">
            {(["F", "L", "R"] as const).map((side) => {
              const url = finger.raw_side_urls?.[side];
              return (
                <figure key={side} className="space-y-1">
                  <figcaption className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                    {SIDE_LABEL[side]} <span className="text-slate-400 normal-case font-normal">· {side}</span>
                  </figcaption>
                  <div className="aspect-square bg-surface-2 border border-surface-3 rounded-xl overflow-hidden flex items-center justify-center text-[10px] text-slate-400 shadow-depth-1">
                    {url ? (
                      <AuthedImage url={url} alt={`sisi ${side}`} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-center p-2">dilewati / tidak ada</span>
                    )}
                  </div>
                </figure>
              );
            })}
          </div>

          {/* Soft warning kalau jumlah delta tidak match pattern's expected count. */}
          <AnimatePresence initial={false}>
            {(() => {
              const nDeltas = finger.deltas?.length ?? 0;
              const expected = EXPECTED_DELTAS[pattern];
              if (nDeltas === expected) return null;
              return (
                <motion.div
                  key="delta-warn"
                  initial={{ opacity: 0, y: -4, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -4, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5"
                >
                  <span aria-hidden>⚠</span>
                  <span>
                    Pola <strong>{pattern}</strong> umumnya {expected} delta — saat ini {nDeltas}.
                    Bu Emmy bisa tambah/hapus delta lewat tombol di gambar.
                  </span>
                </motion.div>
              );
            })()}
          </AnimatePresence>

          <div className="mt-4 space-y-3 text-sm">
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1.5">{t.patternIs}</label>
              <select
                disabled={!canEdit || isApproved}
                value={pattern}
                onChange={(e) => {
                  const p = e.target.value as Pattern;
                  setPattern(p);
                  scheduleSave({ pattern: p });
                }}
                className="input"
              >
                {PATTERNS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3 relative">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1.5">{t.ridge} kiri</label>
                <motion.input
                  type="number"
                  min={0}
                  disabled={!canEdit || isApproved || isRecounting}
                  value={ridgeL}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setRidgeL(v);
                    scheduleSave({ ridge_count_left: v });
                  }}
                  className="input"
                  animate={{ scale: isRecounting ? 0.99 : 1 }}
                  transition={{ duration: 0.15 }}
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1.5">{t.ridge} kanan</label>
                <motion.input
                  type="number"
                  min={0}
                  disabled={!canEdit || isApproved || isRecounting}
                  value={ridgeR}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setRidgeR(v);
                    scheduleSave({ ridge_count_right: v });
                  }}
                  className="input"
                  animate={{ scale: isRecounting ? 0.99 : 1 }}
                  transition={{ duration: 0.15 }}
                />
              </div>
              <AnimatePresence>
                {isRecounting && (
                  <motion.div
                    key="recount"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.15 }}
                    className="col-span-2 inline-flex items-center gap-2 text-[11px] text-brand-700"
                  >
                    <span className="w-3 h-3 border-2 border-brand-300 border-t-brand-600 rounded-full animate-spin" />
                    menghitung sulur…
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {!isApproved && canEdit && (
            <div className="mt-4 space-y-2">
              <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={(e) => setVerified(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 accent-brand-600"
                />
                Saya sudah verifikasi pattern ini
              </label>
              <Button
                variant="success"
                className="w-full"
                disabled={!verified}
                loading={approveMutation.isPending}
                onClick={() => approveMutation.mutate()}
                iconLeft={
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden>
                    <path d="M5 10.5L8.5 14L15 7" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              >
                {t.approve} {FINGER_LABEL_ID[finger.finger as FingerKey]}
              </Button>
            </div>
          )}
        </>
      )}

      {!expanded && isHighConf && (
        <button
          type="button"
          className="text-xs text-brand-600 hover:text-brand-700 underline-offset-2 hover:underline mt-2 inline-flex items-center gap-1.5 transition-colors"
          onClick={() => setExpanded(true)}
        >
          <span>
            {t.pattern}: <strong>{finger.pattern}</strong> · {t.ridge}: {finger.ridge_count_left}/{finger.ridge_count_right}
          </span>
          <span aria-hidden>→</span>
        </button>
      )}
    </Card>
  );
}
